import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Building2, Phone, Calendar, MapPin, CheckCircle, AlertCircle, User, Mail, Image, MapPinned, Loader, LogIn } from 'lucide-react'
import { toast } from 'react-hot-toast'
import axios from 'axios'
import api from '../../config/api'
import { Link } from 'react-router-dom'
import SEOHead from '../SEO/SEOHead'

export default function NGORegistration() {
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    phoneNumber: '',
    email: '',
    password: '',
    personalName: '',
    personalPhone: '',
    pincode: '',
    state: '',
    city: '',
    startDate: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [registeredNGO, setRegisteredNGO] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  
  // State and city data
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [loadingStates, setLoadingStates] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)
  const [stateData, setStateData] = useState(null)
  
  // Fetch states on component mount
  useEffect(() => {
    fetchStates()
  }, [])
  
  // Update cities when state changes
  useEffect(() => {
    if (!formData.state || !stateData) {
      setCities([])
      return
    }
    // Get cities from the cached state data
    const citiesForState = stateData[formData.state] || []
    const cityObjects = citiesForState.map(city => ({ city_name: city }))
    setCities(cityObjects)
  }, [formData.state, stateData])
  
  // Fetch all states and cities data from API
  const fetchStates = async () => {
    setLoadingStates(true)
    try {
      const response = await api.get('/location/states')
      if (response.data.success) {
        // Store the complete state-city data
        setStateData(response.data.data)
        
        // Extract state names for the dropdown
        const stateList = Object.keys(response.data.data)
        const stateObjects = stateList.map(state => ({ state_name: state }))
        setStates(stateObjects)
      } else {
        throw new Error('Failed to fetch states')
      }
    } catch (error) {
      console.error('Error fetching states:', error)
      toast.error('Failed to load states. Please try again.')
      
      // Fallback to static data if API fails
      const indianStates = [
        { state_name: "Andhra Pradesh" }, { state_name: "Maharashtra" }, 
        { state_name: "Karnataka" }, { state_name: "Tamil Nadu" },
        { state_name: "Delhi" }, { state_name: "Uttar Pradesh" },
        { state_name: "Gujarat" }, { state_name: "Telangana" },
        { state_name: "West Bengal" }, { state_name: "Rajasthan" }
      ]
      setStates(indianStates)
    } finally {
      setLoadingStates(false)
    }
  }

  // Handle logo image upload
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo image must be less than 2MB');
        return;
      }

      // Check file type
      if (!file.type.match('image.*')) {
        toast.error('Please select an image file');
        return;
      }

      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Convert to base64 for storage
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        setFormData({...formData, logo: e.target.result});
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    console.log('Form submission started');

    // Validate all required fields
    const requiredFields = [
      'name',
      'phoneNumber', 
      'email',
      'password',
      'personalName',
      'personalPhone',
      'pincode',
      'state',
      'city',
      'startDate'
    ];

    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.join(', ');
      setErrorMessage(`Please complete these required fields: ${missingFieldNames}`);
      toast.error('Please complete all required fields before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Sending NGO data to backend:', formData);

      // Create a custom instance of axios for this request to avoid interceptors
      const axiosInstance = axios.create({
        baseURL: api.defaults.baseURL,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Send data to backend API without using the main api instance
      // This prevents the auth interceptor from redirecting
      const response = await axiosInstance.post('/ngos', formData);
      
      console.log('Response from backend:', response.data);
      
      // Also store in localStorage as a backup
      const ngos = JSON.parse(localStorage.getItem('registeredNGOs') || '[]');
      const newNGO = {
        ...response.data.ngo || response.data,
        id: (response.data.ngo && response.data.ngo._id) || response.data._id || Date.now()
      };
      ngos.push(newNGO);
      localStorage.setItem('registeredNGOs', JSON.stringify(ngos));

      toast.success('NGO registered successfully!');
      
      // Set success message and store registered NGO details
      setSuccessMessage('Your NGO has been registered successfully!');
      setRegisteredNGO(response.data.ngo || response.data);
      
      // Reset form
      setFormData({
        name: '',
        logo: '',
        phoneNumber: '',
        email: '',
        password: '',
        personalName: '',
        personalPhone: '',
        pincode: '',
        state: '',
        city: '',
        startDate: ''
      });
      setLogoPreview(null);
      setErrorMessage('');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error registering NGO:', error);
      console.error('Error details:', error.response?.data);
      
      const errorMsg = error.response?.data?.message || error.message || 'Failed to register NGO. Please try again.';
      setErrorMessage(errorMsg);
      setSuccessMessage('');
      setRegisteredNGO(null);
      
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <SEOHead 
        title="NGO Registration India | Blood Donation Network | BloodHero"
        description="Register your NGO with BloodHero blood donation platform. Join our network of non-governmental organizations supporting blood donation campaigns across India. Create blood donation projects and save lives."
        keywords="NGO registration India, blood donation network, NGO blood donation, blood donation projects, NGO registration BloodHero, blood donation campaigns India, NGO blood bank registration"
        canonicalUrl="/ngo-registration"
        ogImage="/images/ngo-registration-og.jpg"
        ogType="website"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-white pt-20 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            {/* Header Section */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-block p-4 bg-red-50 rounded-full mb-6 shadow-lg"
              >
                <Building2 size={40} className="text-red-600" />
              </motion.div>
              <h1 className="text-4xl font-bold mb-3 text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-red-500">
                NGO Registration
              </h1>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Join our network of non-governmental organizations supporting blood donation
              </p>
              <div className="mt-4">
                <Link to="/ngo/login" className="inline-flex items-center px-4 py-2 bg-white border border-red-300 rounded-lg text-red-600 hover:bg-red-50 transition-colors shadow-sm">
                  <LogIn className="mr-2 h-4 w-4" />
                  Already registered? Login
                </Link>
              </div>
            </div>

            {/* Form Container */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="p-8">
                {successMessage && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-green-800">Success!</h3>
                      <p className="text-green-700 mt-1">{successMessage}</p>
                      {registeredNGO && (
                        <div className="mt-2 p-3 bg-white rounded border border-green-100">
                          <p className="text-gray-700"><strong>NGO Name:</strong> {registeredNGO.name}</p>
                          <p className="text-gray-700"><strong>Email:</strong> {registeredNGO.email}</p>
                          <p className="text-gray-700"><strong>Status:</strong> {registeredNGO.status}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {errorMessage && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-red-800">There was an error with your submission</h3>
                      <p className="text-red-700 mt-1">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* NGO Information Section */}
                  <div>
                    <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center border-b pb-2">
                      <Building2 className="mr-2 text-red-600" /> NGO Information
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center">
                          <Building2 size={16} className="mr-1 text-red-600" />
                          NGO Name <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter NGO name"
                          required
                        />
                      </div>

                      <div className="group">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center">
                          <Phone size={16} className="mr-1 text-red-600" />
                          Contact Number <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="tel"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          value={formData.phoneNumber}
                          onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                          placeholder="Enter contact number"
                          required
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Mail size={16} className="mr-1 text-red-600" />
                        Email Address <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="email"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    
                    <div className="mt-6">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <User size={16} className="mr-1 text-red-600" />
                        Password <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="password"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Enter password (min 6 characters)"
                        minLength="6"
                        required
                      />
                    </div>

                    <div className="mt-6 grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center">
                          <User size={16} className="mr-1 text-red-600" />
                          Personal Contact Name <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          value={formData.personalName}
                          onChange={(e) => setFormData({...formData, personalName: e.target.value})}
                          placeholder="Enter contact person name"
                          required
                        />
                      </div>

                      <div className="group">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center">
                          <Phone size={16} className="mr-1 text-red-600" />
                          Personal Contact Number <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="tel"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          value={formData.personalPhone}
                          onChange={(e) => setFormData({...formData, personalPhone: e.target.value})}
                          placeholder="Enter personal contact number"
                          required
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Image size={16} className="mr-1 text-red-600" />
                        NGO Logo
                      </label>
                      <div className="flex items-start space-x-4">
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            onChange={handleLogoChange}
                          />
                          <p className="text-xs text-gray-500 mt-1">Upload a logo image (max 2MB)</p>
                        </div>
                        {logoPreview && (
                          <div className="w-20 h-20 relative">
                            <img 
                              src={logoPreview} 
                              alt="Logo Preview" 
                              className="w-full h-full object-cover rounded-lg border border-gray-300"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div>
                    <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center border-b pb-2">
                      <MapPinned className="mr-2 text-red-600" /> Location Details
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center">
                          <MapPin size={16} className="mr-1 text-red-600" />
                          Pincode <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                          value={formData.pincode}
                          onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                          placeholder="Enter pincode"
                          required
                        />
                      </div>

                      <div className="group">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center">
                          <MapPinned size={16} className="mr-1 text-red-600" />
                          State <span className="text-red-500 ml-1">*</span>
                        </label>
                        <div className="relative">
                          <select
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
                            value={formData.state}
                            onChange={(e) => setFormData({...formData, state: e.target.value, city: ''})}
                            required
                            disabled={loadingStates}
                          >
                            <option value="">Select State</option>
                            {states.map(state => (
                              <option key={state.state_name} value={state.state_name}>{state.state_name}</option>
                            ))}
                          </select>
                          {loadingStates && (
                            <div className="absolute right-3 top-3">
                              <Loader size={20} className="animate-spin text-red-500" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <MapPinned size={16} className="mr-1 text-red-600" />
                        City <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        {cities.length > 0 ? (
                          <select
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white"
                            value={formData.city}
                            onChange={(e) => setFormData({...formData, city: e.target.value})}
                            required
                            disabled={loadingCities || !formData.state}
                          >
                            <option value="">Select City</option>
                            {cities.map(city => (
                              <option key={city.city_name} value={city.city_name}>{city.city_name}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                            value={formData.city}
                            onChange={(e) => setFormData({...formData, city: e.target.value})}
                            placeholder={formData.state ? "Enter city manually" : "Select a state first"}
                            required
                            disabled={!formData.state}
                          />
                        )}
                        {loadingCities && (
                          <div className="absolute right-3 top-3">
                            <Loader size={20} className="animate-spin text-red-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center border-b pb-2">
                      <Calendar className="mr-2 text-red-600" /> Additional Information
                    </h2>
                    <div className="group">
                      <label className="block text-gray-700 font-medium mb-2 flex items-center">
                        <Calendar size={16} className="mr-1 text-red-600" />
                        NGO Start Date <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="date"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                        value={formData.startDate}
                        onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                        max={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-6 border-t border-gray-100">
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={isSubmitting}
                      className="px-8 py-3 bg-gradient-to-r from-red-600 to-red-500 
                        text-white font-medium rounded-lg flex items-center hover:shadow-lg 
                        transition-shadow duration-200 disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Registering...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-5 w-5" />
                          Register NGO
                        </>
                      )}
                    </motion.button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  )
}
